import { z } from "zod";

import type { AutonomyUpcomingAppointmentCategory } from "@/configuration/groups/autonomy/configuration";
import { EcwEncounterType } from "@/src/lib/ehr/ecw/webecw/encounter-type";
import type { EcwDoctor } from "@/src/lib/ehr/ecw/webecw/endpoints/doctors";
import type { EcwEncounter } from "@/src/lib/ehr/ecw/webecw/endpoints/encounters";
import type { EcwStaff } from "@/src/lib/ehr/ecw/webecw/endpoints/staff";
import type { CareTeamMember } from "@/src/lib/ehr/fhir";
import type { IntakeManagerRegistrationLookup } from "@/src/lib/ehr/intake-manager/intake-manager-integration";
import type { ModMedTaskCreateOutput } from "@/src/lib/ehr/mod-med-task";
import type { ProgressNoteContext } from "@/src/lib/ehr/referral-matching";
import type { ReplyFaxOutput } from "@/src/lib/fax-template/send-reply-fax";
import type { CobaltStaffList } from "@/src/lib/web-automation/cobalt/cobalt-types";
import type { AssigneeResolution } from "@/src/lib/workflows/steps/assignee-resolution";
import type {
  CcTeCreationDispatch,
  CcTeCreationOutput,
} from "@/src/lib/workflows/steps/cloudcruise/ecw/cc-te-creation";
import type { OrderAttachOutput } from "@/src/lib/workflows/steps/cloudcruise/ecw/order-attach";
import type { OrderUpdateOutput } from "@/src/lib/workflows/steps/cloudcruise/ecw/order-update";
import type { ReferralAttachOutput } from "@/src/lib/workflows/steps/cloudcruise/ecw/referral-attach";
import type { ReferralClosureOutput } from "@/src/lib/workflows/steps/cloudcruise/ecw/referral-closure";
import type { CobaltAppointmentFetchOutput } from "@/src/lib/workflows/steps/cobalt/appointment-fetch";
import type { CobaltDocUploadOutput } from "@/src/lib/workflows/steps/cobalt/doc-upload";
import type { CobaltPatientFetchOutput } from "@/src/lib/workflows/steps/cobalt/patient-fetch";
import type { CreateTelEncounterOutput } from "@/src/lib/workflows/steps/cobalt/tel-enc";
import type { AthenaInsuranceEligibilityStepOutput } from "@/src/lib/workflows/steps/ehr/athena-insurance-eligibility";
import type { AthenaReferralDocumentMetadataOutput } from "@/src/lib/workflows/steps/ehr/athena-referral-disposition";
import type { AthenaSyncCoveragesOutput } from "@/src/lib/workflows/steps/ehr/athena-sync-coverages";
import type {
  ClinicientAuthorizationOutput,
  ClinicientAuthorizationDocumentOutput,
  ClinicientAuthorizationTaskOutput,
} from "@/src/lib/workflows/steps/ehr/clinicient-authorization";
import type {
  ClinicientCaseDocumentOutput,
  ClinicientCaseDocumentRejected,
} from "@/src/lib/workflows/steps/ehr/clinicient-case-document";
import type { ClinicientCaseTaskOutput } from "@/src/lib/workflows/steps/ehr/clinicient-case-task";
import type { ClinicientChartNoteStepOutput } from "@/src/lib/workflows/steps/ehr/clinicient-chart-note";
import type { ClinicientInsuranceOutput } from "@/src/lib/workflows/steps/ehr/clinicient-insurance";
import type { ClinicientPatientCaseOutput } from "@/src/lib/workflows/steps/ehr/clinicient-patient-case";
import type { NextGenTaskCreateOutput } from "@/src/lib/workflows/steps/ehr/create-nextgen-task";
import type { PatientCaseOutput } from "@/src/lib/workflows/steps/ehr/create-patient-case";
import type { EhrDocUploadOutput, EhrDocUploadUnmapped } from "@/src/lib/workflows/steps/ehr/ehr-doc-upload";
import type { EnsurePatientOutput } from "@/src/lib/workflows/steps/ehr/ensure-patient";
import type { FetchPcpOutput } from "@/src/lib/workflows/steps/ehr/fetch-pcp";
import type { IntakeManagerQuestionnaireOutput } from "@/src/lib/workflows/steps/ehr/intake-manager-registration";
import type { MedentDocUploadOutput } from "@/src/lib/workflows/steps/ehr/medent-doc-upload";
import type { NextGenLastQualifyingEncounterOutput } from "@/src/lib/workflows/steps/ehr/nextgen-last-qualifying-encounter-fetch";
import type { OrderMatchOutput } from "@/src/lib/workflows/steps/ehr/order-match";
import type { ReferralsFetchOutput } from "@/src/lib/workflows/steps/ehr/referrals-fetch";
import type { ResolveOrderingProviderOutput } from "@/src/lib/workflows/steps/ehr/resolve-ordering-provider";
import type { SectionsUploadOutput, SectionUploadOutput } from "@/src/lib/workflows/steps/ehr/section-upload";
import type { PreflightOutput } from "@/src/lib/workflows/steps/preflight";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { SchedulingRecommendationOutput } from "@/src/lib/workflows/steps/referral/scheduling-recommendation";
import type { SendReferralEmailOutput } from "@/src/lib/workflows/steps/referral/send-referral-email";
import type { SetTaskPriorityResult } from "@/src/lib/workflows/steps/task/set-task-priority";
import type { EcwAutoAssignOutput } from "@/src/lib/workflows/steps/webecw/auto-assign";
import type { WebecwCoverageOutput } from "@/src/lib/workflows/steps/webecw/coverage";
import type { WebecwReferralPatientCreationOutput } from "@/src/lib/workflows/steps/webecw/create-referral-patient";
import type { WebecwDiOrderUpdateOutput } from "@/src/lib/workflows/steps/webecw/di-order-update";
import type { WebecwDocUploadOutput } from "@/src/lib/workflows/steps/webecw/doc-upload";
import type { WebecwReferralOrderOutput } from "@/src/lib/workflows/steps/webecw/referral-order";
import type { WebecwReferringProviderUpdateOutput } from "@/src/lib/workflows/steps/webecw/referring-provider-step";
import type { WebecwRenderingProviderUpdateOutput } from "@/src/lib/workflows/steps/webecw/rendering-provider-step";
import type {
  CreateWebecwTelEncOutput,
  ReferralTelEncInstructionsOutput,
  WebecwTelEncOutcome,
} from "@/src/lib/workflows/steps/webecw/tel-enc-contract";
import type { Appointment } from "@/src/models/appointment";
import {
  insuranceNetworkStatusSchema,
  insuranceNetworkVerificationSchema,
  insuranceRequirementStatusSchema,
  providerInsuranceRequirementsSchema,
} from "@/src/models/provider-insurance-requirements";
import type { ReferralTierOutput, SmsEligibilityOutput } from "@/src/models/referral-eligibility";
import type { ReplyFaxPdfOutput, ReplyFaxRoutingOutput } from "@/src/models/reply-fax";
import { stediEligibilityStatusSchema } from "@/src/models/stedi-eligibility-status";

/*
 * Customer-step output contracts.
 *
 * These shapes are produced by tenant-specific match steps living under
 * `configuration/groups/<sub>/`. Their definitions stay in this file (rather
 * than imported from the customer dir) so the platform's `step-outputs.ts`
 * never needs to import from `configuration/groups/**` (see the dependency
 * arrow in docs/agent-rules/workflow-steps-v2.md §3).
 *
 * Customer step files import these types from here.
 */

export enum AutonomyChartEnrichmentOutcome {
  ACTION_REQUIRED = "action_required",
  APPLIED_OR_NOT_NEEDED = "applied_or_not_needed",
}

const autonomyChartEnrichmentIdSchema = z.string().trim().min(1);
const autonomyChartEnrichmentProviderNameSchema = z.string().trim().min(1);

const autonomyRenderingMismatchSchema = z
  .object({
    existingDoctorId: autonomyChartEnrichmentIdSchema,
    existingProviderName: autonomyChartEnrichmentProviderNameSchema,
    referralProviderName: autonomyChartEnrichmentProviderNameSchema,
  })
  .strict();

export const autonomyChartEnrichmentOutputSchema = z
  .object({
    outcome: z.nativeEnum(AutonomyChartEnrichmentOutcome),
    renderingMismatch: autonomyRenderingMismatchSchema.nullable(),
    referringProviderId: autonomyChartEnrichmentIdSchema.nullable(),
    renderingProviderId: autonomyChartEnrichmentIdSchema.nullable(),
  })
  .strict();

export type AutonomyChartEnrichmentOutput = z.infer<typeof autonomyChartEnrichmentOutputSchema>;

export enum TcoBodyPartGrouping {
  FOOT_AND_ANKLE = "Foot & Ankle",
  HAND_AND_WRIST = "Hand & Wrist",
  KNEE = "Knee",
  HIP = "Hip",
  SHOULDER = "Shoulder",
  MISC = "Misc",
}

export enum TcoBodySide {
  LEFT = "Left",
  RIGHT = "Right",
  BILATERAL = "Bilateral",
}

export enum TcoExternalImagingTaskAction {
  CREATE_TASK = "create_task",
  NO_TASK = "no_task",
}

export enum TcoExternalImagingReason {
  SURGEON_NURSE = "surgeon_nurse",
  NO_SURGERY_IN_WINDOW = "no_surgery_in_window",
  NO_BODY_PART_MATCH = "no_body_part_match",
  SIDE_CONFLICT = "side_conflict",
  UNCONFIRMED_SIDE = "unconfirmed_side",
  UNROUTABLE_SURGEON = "unroutable_surgeon",
  CONFLICTING_SURGEONS = "conflicting_surgeons",
  UNCOMPARABLE_BODY_PART = "uncomparable_body_part",
  OPEN_REFERRAL_TASK = "open_referral_task",
}

const tcoExternalImagingCreateTaskSchema = z.object({
  action: z.literal(TcoExternalImagingTaskAction.CREATE_TASK),
  assignee: z.string().min(1),
});
const tcoExternalImagingNoTaskSchema = z.object({ action: z.literal(TcoExternalImagingTaskAction.NO_TASK) });
const tcoExternalImagingSurgerySummarySchema = z.object({
  appointmentId: z.string().nullable(),
  appointmentTypeName: z.string(),
  start: z.string(),
  regions: z.array(z.nativeEnum(TcoBodyPartGrouping)),
  side: z.nativeEnum(TcoBodySide).nullable(),
  surgeonName: z.string().nullable(),
});

export const tcoExternalImagingDecisionSchema = z.discriminatedUnion("reason", [
  z.object({
    reason: z.literal(TcoExternalImagingReason.SURGEON_NURSE),
    task: tcoExternalImagingCreateTaskSchema,
    surgery: tcoExternalImagingSurgerySummarySchema,
  }),
  z.object({ reason: z.literal(TcoExternalImagingReason.NO_SURGERY_IN_WINDOW), task: tcoExternalImagingNoTaskSchema }),
  z.object({ reason: z.literal(TcoExternalImagingReason.NO_BODY_PART_MATCH), task: tcoExternalImagingNoTaskSchema }),
  z.object({ reason: z.literal(TcoExternalImagingReason.SIDE_CONFLICT), task: tcoExternalImagingNoTaskSchema }),
  z.object({
    reason: z.literal(TcoExternalImagingReason.UNCONFIRMED_SIDE),
    task: tcoExternalImagingCreateTaskSchema,
    surgery: tcoExternalImagingSurgerySummarySchema,
  }),
  z.object({
    reason: z.literal(TcoExternalImagingReason.UNROUTABLE_SURGEON),
    task: tcoExternalImagingCreateTaskSchema,
    surgery: tcoExternalImagingSurgerySummarySchema,
  }),
  z.object({
    reason: z.literal(TcoExternalImagingReason.CONFLICTING_SURGEONS),
    task: tcoExternalImagingCreateTaskSchema,
  }),
  z.object({
    reason: z.literal(TcoExternalImagingReason.UNCOMPARABLE_BODY_PART),
    task: tcoExternalImagingCreateTaskSchema,
  }),
  z.object({ reason: z.literal(TcoExternalImagingReason.OPEN_REFERRAL_TASK), task: tcoExternalImagingNoTaskSchema }),
]);

export type TcoExternalImagingDecision = z.infer<typeof tcoExternalImagingDecisionSchema>;
export type TcoExternalImagingCreateTask = z.infer<typeof tcoExternalImagingCreateTaskSchema>;
export type TcoExternalImagingSurgerySummary = z.infer<typeof tcoExternalImagingSurgerySummarySchema>;

/** createAthenaUploadMissingInfoFaxStep: the uploaded missing-info packet's Athena document. */
export type AthenaUploadMissingInfoFaxOutput = {
  documentEhrId: string;
  originalFilename: string;
  missingInfoList: string[];
};

/**
 * Inputs the platform `referralClosureStep` reads from the upstream Pinnacle
 * `referralOpenCheckStep` output. Produced by Pinnacle's customer step;
 * consumed by the platform CC closure step.
 */
export type ReferralClosureInputs = {
  patientMrn: string;
  appointmentDate: string;
  referralStartDate: string;
  referralReasonCode: string;
  assignedTo: string | null;
  specialty?: string | null;
};

export enum ReferralOpenCheckOutcome {
  MATCHED_FOR_CLOSURE = "matched_for_closure",
  ROUTE_TO_REVIEW = "route_to_review",
  NOTE_TOO_OLD = "note_too_old",
  NO_OPEN_REFERRALS = "no_open_referrals",
  NO_REFERRAL_MATCH = "no_referral_match",
}

export type ReferralOpenCheckOutputOutcome =
  | ReferralOpenCheckOutcome.MATCHED_FOR_CLOSURE
  | ReferralOpenCheckOutcome.NOTE_TOO_OLD
  | ReferralOpenCheckOutcome.NO_OPEN_REFERRALS
  | ReferralOpenCheckOutcome.NO_REFERRAL_MATCH;

/** Pinnacle CONSULT_REFERRALS_WF: open-check step output. */
export type ReferralOpenCheckOutput = {
  outcome?: ReferralOpenCheckOutputOutcome;
  referralClosureInputs?: ReferralClosureInputs;
};

export type ReferralMatchReferralSummary = {
  performerName: string | null;
  performerAddress: string | null;
  requesterName: string | null;
  specialty: string | null;
  reasonText: string | null;
  icdCodes: string[];
  cptCodes: string[];
  startDate: string | null;
  endDate: string | null;
  status: string | null;
};

/** PA East OUTGOING_REFERRALS_WF: match step output. */
export type ReferralMatchOutput = {
  matched: boolean;
  outcome: string;
  patientMrn?: string;
  referralReason?: string;
  referralStartDate?: string;
  orderingProviderEhrId?: string;
  matchedSpecialty?: string | null;
  matchedCptCodes?: string[];
  matchedReferral?: ReferralMatchReferralSummary;
  candidateReferrals?: ReferralMatchReferralSummary[];
  matchedCount?: number;
  reasoning?: string[];
  unableToSchedule?: boolean;
  cannotServicePatient?: boolean;
  earlySteps?: boolean;
  intakeEvidence?: ProgressNoteContext;
};

export enum PaEastRoutingOutcome {
  DUPLICATE_POC = "duplicate_poc",
  DELETE = "delete",
  NOOP = "noop",
}

export type PaEastRoutingOutput = {
  outcome: PaEastRoutingOutcome;
  reason?: string;
};

export enum PaEastAssigneeUnresolvedReason {
  NO_ASSIGNMENT = "no_assignment",
  NO_AUTO_ASSIGN = "no_auto_assign",
  ROSTER_GAP = "roster_gap",
}

export type PaEastAssigneeResolution =
  | { resolved: true; staffId: string; name: string }
  | { resolved: false; reason: PaEastAssigneeUnresolvedReason };

export type PaEastWellChildCheckOutput = {
  lastWellChildCheckDate: string | null;
  isMedicallyComplex: boolean;
  isHighRiskDiagnosis: boolean;
  isPastDue: boolean;
  topDiagnosisNames: string[];
};

/**
 * ENTP procedure_sleep encounter-context step output. Runs between webecw provider-context and
 * resolveAssignee — derives the nearest-upcoming sleep encounter date so doc-upload notes can
 * reference it without preflight itself reading provider-context.
 */
export type EntpSleepEncounterContextOutput = {
  nearestUpcomingSleepAppointmentDate: string | null;
};

/**
 * ENTP DME encounter-context step output. Runs between webecw provider-context and resolveAssignee.
 * Nearest-past drives CMN filename + isReviewed + notes; nearest-upcoming plus DME order type drives
 * the tel-enc step's should-create decision.
 */
export type EntpDmeEncounterContextOutput = {
  nearestPastDmeAppointmentDate: string | null;
  nearestUpcomingDmeAppointmentDate: string | null;
};

export type WebecwProviderContext = {
  staff: EcwStaff[];
  doctors: EcwDoctor[];
  // Internal eCW `pid` resolved from the preflight MRN. Optional so provider-context outputs
  // persisted before this field existed remain readable by downstream steps.
  webecwPatientId?: string | null;
  // Raw patient encounters — surfaced so downstream steps can filter/select without a second fetch.
  // Optional so persisted task_step rows from before this field existed still deserialize; new rows
  // always populate it (empty array when MRN could not be resolved).
  encounters?: EcwEncounter[];
  encounterDoctorId: string | null;
  encounterFacilityId?: string | null;
  renderingProviderId: string | null;
  pcpId: string | null;
  upcomingAppointmentCategory: AutonomyUpcomingAppointmentCategory | null;
  careTeam?: CareTeamMember[];
};

export const persistedWebecwEncounterRecencySchema = z.object({
  encounterID: z.string(),
  date: z.string().nullable(),
  encType: z.nativeEnum(EcwEncounterType),
  status: z.string(),
  visitType: z.string(),
  doctorID: z.string(),
});

/**
 * Output persisted by a `createWaitUntilStep` step across pauses.
 * @property firstPausedAt ISO timestamp of the first pause, used to measure total elapsed wait.
 * @property attempts Number of times the step has paused (1 on the first pause).
 */
export type WaitUntilOutput = {
  firstPausedAt: string;
  attempts: number;
  firedEventKeys?: string[];
};

/** Balance Health: most-recent encounter's ModMed Location FHIR id (null when unknown). */
export type BalanceEncounterLocationOutput = {
  encounterLocationId: string | null;
};

/** ModMed: provider (NPI + name) on the patient's upcoming or most-recent appointment; nulls when unresolved. */
export type AppointmentProviderOutput = {
  npi: string | null;
  familyName: string | null;
  givenName: string | null;
};

export type NextAppointmentOutput = {
  appointmentDate: string | null;
  practitionerName: string | null;
};

export type OpenReferralCheckOutput = {
  hasOpenReferralTask?: boolean;
  multipartDocumentLabel?: string;
  firstPausedAt?: string;
  attempts?: number;
};

export type SameDayCaseInheritanceOutput = {
  inherited: boolean;
  inheritedCaseEhrId?: string;
  firstPausedAt?: string;
  attempts?: number;
  wakeKey?: number;
};

/**
 * Result of the shared `checkInsuranceEligibilityStep`, surfaced so downstream steps (e.g. a
 * referral task note) can report the Stedi eligibility outcome. `checkFailed`/`skipped` flag the
 * "unable to confirm" case; `eligibilityStatus` is the persisted Stedi status when a check ran.
 */
export const checkInsuranceEligibilityOutputSchema = z
  .object({
    skipped: z.boolean(),
    checkFailed: z.boolean(),
    payerIdentifier: z.string().trim().min(1).nullable().optional(),
    eligibilityStatus: stediEligibilityStatusSchema.nullable(),
    payerName: z.string().nullable(),
    planType: z.string().nullable(),
    planName: z.string().nullable().optional(),
    officeVisitRequiresAuth: z.boolean().nullable(),
  })
  .strict();

export const persistedCheckInsuranceEligibilityOutputSchema = checkInsuranceEligibilityOutputSchema
  .partial({ officeVisitRequiresAuth: true })
  .strict();

export type CheckInsuranceEligibilityOutput = z.infer<typeof checkInsuranceEligibilityOutputSchema>;

export type OpenTaskCheckOutput = {
  hasOpenTask: boolean;
};

export type AthenaInsurancePackageResolutionSource = "athena_search" | "configured" | "confirmed_catalog" | "existing";

export type CreateCoverageOutput = {
  unresolvedPayer?: string;
  unwrittenCoverages?: string[];
  manuallyCompleted?: boolean;
  writtenCoverages?: Array<{
    coverageId: string;
    insurancePackageEhrId: string;
    order: number;
    resolutionSource?: AthenaInsurancePackageResolutionSource;
  }>;
};

export type SyncPatientAppointmentsOutput = {
  appointments: Array<{
    appointmentDatetime: string | null;
    locationEhrId: string | null;
    providerEhrId: string | null;
    status: Appointment["status"];
  }>;
};

export const athenaCoverageReadbackOutputSchema = z.object({
  coverages: z.array(
    z.object({
      insuranceId: z.string(),
      insurancePackageId: z.string(),
      planName: z.string().nullable(),
      sequence: z.number().int().positive().nullable(),
      subscriberId: z.string().nullable(),
      selfPay: z.boolean(),
      casePolicy: z.boolean(),
    })
  ),
  primary: z.object({
    insuranceId: z.string(),
    insurancePackageId: z.string(),
    planName: z.string().nullable(),
    selfPay: z.boolean(),
    casePolicy: z.boolean(),
  }),
  secondary: z
    .object({
      insuranceId: z.string(),
      insurancePackageId: z.string(),
      planName: z.string().nullable(),
      casePolicy: z.boolean(),
    })
    .nullable(),
});
export type AthenaCoverageReadbackOutput = z.infer<typeof athenaCoverageReadbackOutputSchema>;

export const tprInsuranceNetworkStatusSchema = insuranceNetworkStatusSchema;
export type TprInsuranceNetworkStatus = z.infer<typeof tprInsuranceNetworkStatusSchema>;

export const tprInsuranceRequirementStatusSchema = insuranceRequirementStatusSchema;
export type TprInsuranceRequirementStatus = z.infer<typeof tprInsuranceRequirementStatusSchema>;

export const tprInsuranceRequirementsOutputSchema = providerInsuranceRequirementsSchema.extend({
  networkVerification: insuranceNetworkVerificationSchema.optional(),
  networkVariantKey: z.string().nullable().optional(),
});
export type TprInsuranceRequirementsOutput = z.infer<typeof tprInsuranceRequirementsOutputSchema>;

export const tprReferralAuthorizationOutputSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("not_needed"),
    reason: z.enum(["self_pay", "ineligible", "network_blocked", "requirements_unresolved", "not_required"]),
  }),
  z.object({
    outcome: z.literal("needs_review"),
    reason: z.literal("requirements_unresolved"),
  }),
  z.object({
    outcome: z.literal("needs_pending_request"),
    reason: z.enum(["missing_number", "not_valid_for_lookahead"]),
  }),
  z.object({
    outcome: z.literal("created"),
    write: z.enum(["paper", "authorization"]),
    referralAuthId: z.string(),
    alreadyExisted: z.boolean(),
    pending: z.boolean(),
    appointmentAttached: z.boolean().optional().default(false),
    needsManualAppointmentAttach: z.boolean().optional().default(false),
  }),
]);
export type TprReferralAuthorizationOutput = z.infer<typeof tprReferralAuthorizationOutputSchema>;

export const tprReferralAuthRequestUploadOutputSchema = z.object({
  uploaded: z.boolean(),
  documentEhrId: z.string().nullable(),
});
export type TprReferralAuthRequestUploadOutput = z.infer<typeof tprReferralAuthRequestUploadOutputSchema>;

export type StepOutputs = {
  hubspot_patient_update?: { enqueued: boolean };
  resolve_reply_fax?: ReplyFaxRoutingOutput;
  build_reply_fax?: ReplyFaxPdfOutput;
  send_reply_fax?: ReplyFaxOutput;
  require_patient_phone?: WaitUntilOutput | ReplyFaxOutput;
  require_document_signature?: ReplyFaxOutput;
  determine_patient_tier?: ReferralTierOutput;
  sms_eligibility?: SmsEligibilityOutput;
  clinicient_ensure_authorization?: ClinicientAuthorizationOutput;
  clinicient_authorization_document_upload?: ClinicientAuthorizationDocumentOutput;
  clinicient_authorization_task?: ClinicientAuthorizationTaskOutput;
  referral_sheet_export?: { exportedAt: string; updatedRange: string };
  clinicient_case_document_upload?: ClinicientCaseDocumentOutput | ClinicientCaseDocumentRejected;
  clinicient_case_task?: ClinicientCaseTaskOutput;
  clinicient_document_note?: ClinicientChartNoteStepOutput | ClinicientCaseDocumentRejected;
  clinicient_ensure_case?: ClinicientPatientCaseOutput;
  clinicient_same_day_case_inheritance?: SameDayCaseInheritanceOutput;
  clinicient_same_day_phone_inheritance?: WaitUntilOutput;
  clinicient_patient_note?: ClinicientChartNoteStepOutput;
  clinicient_sync_insurance?: ClinicientInsuranceOutput;
  autonomy_chart_enrichment?: AutonomyChartEnrichmentOutput;
  create_coverage?: CreateCoverageOutput;
  cobalt_appointment_fetch?: CobaltAppointmentFetchOutput;
  cobalt_doc_upload?: CobaltDocUploadOutput;
  balance_encounter_location?: BalanceEncounterLocationOutput;
  fetch_appointment_provider?: AppointmentProviderOutput;
  cobalt_patient_fetch?: CobaltPatientFetchOutput;
  cobalt_staff_list?: CobaltStaffList;
  webecw_auto_assign?: EcwAutoAssignOutput;
  webecw_coverage?: WebecwCoverageOutput;
  webecw_di_order_update?: WebecwDiOrderUpdateOutput;
  webecw_doc_upload?: WebecwDocUploadOutput;
  cobalt_tel_enc?: CreateTelEncounterOutput;
  webecw_tel_enc?: CreateWebecwTelEncOutput | ReferralTelEncInstructionsOutput | WebecwTelEncOutcome;
  entp_sleep_encounter_context?: EntpSleepEncounterContextOutput;
  entp_dme_encounter_context?: EntpDmeEncounterContextOutput;
  webecw_provider_context?: WebecwProviderContext;
  webecw_referral_order?: WebecwReferralOrderOutput;
  webecw_referral_patient_creation?: WebecwReferralPatientCreationOutput;
  webecw_referring_provider_update?: WebecwReferringProviderUpdateOutput;
  webecw_rendering_provider_update?: WebecwRenderingProviderUpdateOutput;
  resolve_assignee?: AssigneeResolution;
  preflight?: PreflightOutput;
  ehr_doc_upload?: EhrDocUploadOutput | EhrDocUploadUnmapped;
  ensure_patient?: EnsurePatientOutput;
  usrc_registration_lookup?: IntakeManagerRegistrationLookup;
  usrc_registration_questionnaire?: IntakeManagerQuestionnaireOutput;
  create_patient_case?: PatientCaseOutput;
  section_upload?: SectionUploadOutput;
  supporting_sections_upload?: SectionsUploadOutput;
  medent_doc_upload?: MedentDocUploadOutput;
  fetch_pcp?: FetchPcpOutput;
  order_attach?: OrderAttachOutput;
  order_match?: OrderMatchOutput;
  order_update?: OrderUpdateOutput;
  // Also used by PA Central (see StepKey in types.ts) — StepOutputs is scoped per task.
  pa_east_assignee_resolution?: PaEastAssigneeResolution;
  pa_east_routing?: PaEastRoutingOutput;
  pa_east_well_child_check?: PaEastWellChildCheckOutput;
  referral_attach?: ReferralAttachOutput;
  referral_closure?: ReferralClosureOutput;
  referral_document_metadata?: AthenaReferralDocumentMetadataOutput;
  referral_match?: ReferralMatchOutput;
  referral_open_check?: ReferralOpenCheckOutput;
  referrals_fetch?: ReferralsFetchOutput;
  nextgen_last_qualifying_encounter_fetch?: NextGenLastQualifyingEncounterOutput;
  scheduling_recommendation?: SchedulingRecommendationOutput;
  send_referral_email?: SendReferralEmailOutput;
  modmed_fetch_next_appointment?: NextAppointmentOutput;
  check_insurance_eligibility?: CheckInsuranceEligibilityOutput;
  exit_if_open_referral?: OpenReferralCheckOutput;
  tco_external_imaging_surgery_match?: TcoExternalImagingDecision;
  modmed_open_task_check?: OpenTaskCheckOutput;
  modmed_api_task_create?: ModMedTaskCreateOutput;
  nextgen_api_task_create?: NextGenTaskCreateOutput;
  cc_metadata_setup?: { fileName: string };
  cc_metadata_update?: { fileName: string };
  cc_te_creation?: CcTeCreationDispatch | CcTeCreationOutput;
  resolve_ordering_provider?: ResolveOrderingProviderOutput;
  resolve_receiving_assignment?: ReceivingAssignmentOutput;
  resolve_tpr_insurance_requirements?: TprInsuranceRequirementsOutput;
  read_athena_coverage?: AthenaCoverageReadbackOutput;
  set_task_priority?: SetTaskPriorityResult;
  athena_upload_missing_info_fax?: AthenaUploadMissingInfoFaxOutput;
  submit_athena_insurance_eligibility?: AthenaInsuranceEligibilityStepOutput;
  sync_athena_coverages?: AthenaSyncCoveragesOutput;
  sync_patient_appointments?: SyncPatientAppointmentsOutput;
  tpr_referral_auth_request_upload?: TprReferralAuthRequestUploadOutput;
  tpr_referral_authorization?: TprReferralAuthorizationOutput;
};

export type StepOutput = NonNullable<StepOutputs[keyof StepOutputs]> | WaitUntilOutput;
